import React from "react";
import { Table } from "@src/common/components/Table";
import CourieButton from "@src/common/components/Button/Button";

export interface TripStopInfo {
  companyName?: string;
  address: string;
}

export interface TripDistanceData {
  origin: TripStopInfo;
  destination: TripStopInfo;
  formattedDistance: string;
  // Present only for live order legs, which support Override/Clear (see
  // onOverride below) — recurring-shipment-template legs don't have stops in
  // this sense, so they leave these undefined.
  sourceStopId?: string;
  destinationStopId?: string;
  distanceInMeters?: number;
  displayDistance?: number;
  isOverridden?: boolean;
}

interface TripDistancesTableProps {
  trips: TripDistanceData[];
  onOverride?: (trip: TripDistanceData) => void;
}

function formatStopInfo(stop: TripStopInfo) {
  return (
    <div className="flex flex-col gap-1">
      {stop.companyName && (
        <div className="font-medium text-sm">{stop.companyName}</div>
      )}
      <div className="text-sm text-gray-600">{stop.address}</div>
    </div>
  );
}

export function TripDistancesTable({
  trips,
  onOverride,
}: TripDistancesTableProps) {
  const columnCount = onOverride ? 4 : 3;

  return (
    <div className="overflow-x-auto">
      <Table>
        <Table.Head>
          <Table.HeadCell>From</Table.HeadCell>
          <Table.HeadCell>To</Table.HeadCell>
          <Table.HeadCell>Distance</Table.HeadCell>
          {onOverride && <Table.HeadCell>Action</Table.HeadCell>}
        </Table.Head>
        <Table.Body className="divide-y">
          {trips.length === 0 ? (
            <Table.Row>
              <Table.Cell colSpan={columnCount} className="text-center">
                No trip distances available.
              </Table.Cell>
            </Table.Row>
          ) : (
            trips.map((trip, index) => (
              <Table.Row key={index}>
                <Table.Cell className="w-2/5">
                  {formatStopInfo(trip.origin)}
                </Table.Cell>
                <Table.Cell className="w-2/5">
                  {formatStopInfo(trip.destination)}
                </Table.Cell>
                <Table.Cell className="w-1/5">
                  <div className="font-medium">{trip.formattedDistance}</div>
                </Table.Cell>
                {onOverride && (
                  <Table.Cell>
                    <CourieButton
                      size="xs"
                      color="gray"
                      onClick={() => onOverride(trip)}
                    >
                      {trip.isOverridden ? "Clear" : "Override"}
                    </CourieButton>
                  </Table.Cell>
                )}
              </Table.Row>
            ))
          )}
        </Table.Body>
      </Table>
    </div>
  );
}
